import { useState, useEffect } from 'react'
import {
  getPhotoSessions,
  createPhotoSession,
  updatePhotoSession,
  moveMediaToSession,
  deletePhotoSession,
  createMediaGroup,
  updateMediaGroup,
  deleteMediaGroup,
  reorderMediaGroups,
  addMediaToGroup,
  removeMediaFromGroup,
  removeMediaFromPhotoSession,
  reorderGroupMedia,
  reorderPhotoSessionMedia,
  reorderPhotoSessions,
} from '../api'
import { getGroupMissingForSale, isGroupReadyForSale, UNASSIGNED_KEY } from '../pages/ModelDetail/utils'
import { useToast } from '../contexts/ToastContext'
import { useI18n } from '../i18n'
import { getApiErrorMessage } from '../api'
import { emptyScriptPricing, scriptPricingForm, scriptPricingPayload } from '../lib/scriptPricing'

/**
 * Управление фотосессиями: загрузка, создание, редактирование, перемещение медиа.
 *
 * @param {string|number} modelId
 * @param {object|null} model
 * @param {function} onModelUpdate
 * @param {Set} selectedIds  — выбранные медиа (из useMediaSelection)
 * @param {function} setMedia  — для обновления photo_session_id у медиа после перемещения
 * @param {function} clearSelection
 * @param {function} loadMedia  — перезагрузить медиа после перемещения (чтобы получить media_group_id)
 */
export default function useSessions(modelId, model, onModelUpdate, selectedIds, setMedia, clearSelection, loadMedia) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [sessions, setSessions] = useState([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [moveTargetSessionId, setMoveTargetSessionId] = useState(null)
  const [moving, setMoving] = useState(false)
  const [showSessionEditModal, setShowSessionEditModal] = useState(null)
  const [editingSessionName, setEditingSessionName] = useState('')
  const [editingSessionDescription, setEditingSessionDescription] = useState('')
  const [editingSessionTags, setEditingSessionTags] = useState('')
  const [editingSessionTimeTag, setEditingSessionTimeTag] = useState('')
  const [editingSessionIsScript, setEditingSessionIsScript] = useState(false)
  const [editingSessionPaidSexting, setEditingSessionPaidSexting] = useState(emptyScriptPricing)
  const [savingSessionName, setSavingSessionName] = useState(false)
  const [groupToEdit, setGroupToEdit] = useState(null)
  const [showCreateSessionInput, setShowCreateSessionInput] = useState(false)
  const [newSessionName, setNewSessionName] = useState('')
  const [newSessionIsScript, setNewSessionIsScript] = useState(false)
  const [newSessionPaidSexting, setNewSessionPaidSexting] = useState(emptyScriptPricing)
  const [creatingSession, setCreatingSession] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState(null)
  const [deletingSession, setDeletingSession] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [deletingGroup, setDeletingGroup] = useState(false)

  function loadSessions() {
    if (!modelId) return
    setLoadingSessions(true)
    getPhotoSessions(modelId)
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : []
        setSessions(list)
      })
      .catch(() => setSessions([]))
      .finally(() => setLoadingSessions(false))
  }

  function scrollToSessionGroup(targetKey) {
    requestAnimationFrame(() => {
      const el = document.getElementById(`media-group-${targetKey}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  async function handleMoveToSession() {
    const sessionId =
      moveTargetSessionId === '' || moveTargetSessionId == null ? null : Number(moveTargetSessionId)
    const ids = Array.from(selectedIds)
    if (!ids.length) return
    setMoving(true)
    onModelUpdate?.({ error: '' })
    try {
      const res = await moveMediaToSession(modelId, ids, sessionId)
      const moved = res?.moved ?? 0
      if (sessionId) {
        loadSessions()
        loadMedia?.() // перезагрузить медиа после перемещения
      } else {
        setMedia((prev) => {
          if (!prev) return prev
          return prev.map((m) =>
            ids.includes(m.id)
              ? { ...m, photo_session_id: null, media_group_id: null, group_sort_order: null }
              : m
          )
        })
      }
      clearSelection()
      setMoveTargetSessionId(null)
      scrollToSessionGroup(sessionId == null ? UNASSIGNED_KEY : String(sessionId))
      const toastMsg = moved > 0
        ? (sessionId ? t('toast.media.movedToSessionCount', { count: moved }) : t('toast.media.unlinkedFromSessionCount', { count: moved }))
        : (sessionId ? t('toast.media.movedToSession') : t('toast.media.unlinkedFromSession'))
      addToast(toastMsg, 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    } finally {
      setMoving(false)
    }
  }

  async function handleCreateSessionAndMove() {
    const name = newSessionName.trim() || 'Новая сессия'
    setCreatingSession(true)
    onModelUpdate?.({ error: '' })
    try {
      const created = await createPhotoSession(modelId, name, newSessionIsScript, {
        paid_sexting_config: scriptPricingPayload(newSessionPaidSexting, newSessionIsScript),
      })
      const sess = created?.data ?? created
      const sessionId = sess?.id
      if (!sessionId) throw new Error('Не удалось создать сессию')
      setSessions((prev) => [...prev, sess])
      const ids = Array.from(selectedIds)
      const moveRes = await moveMediaToSession(modelId, ids, sessionId)
      const moved = moveRes?.moved ?? ids.length
      loadSessions() // обновить сессии с группами
      loadMedia?.() // перезагрузить медиа после перемещения
      setShowCreateSessionInput(false)
      setNewSessionName('')
      setNewSessionIsScript(false)
      setNewSessionPaidSexting(emptyScriptPricing())
      clearSelection()
      scrollToSessionGroup(String(sessionId))
      addToast(moved > 0 ? t('toast.session.createdWithMediaCount', { count: moved }) : t('toast.session.createdWithMedia'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.createSessionFailed'))
      onModelUpdate?.({ error: msg })
    } finally {
      setCreatingSession(false)
    }
  }

  function openSessionEdit(session) {
    setShowSessionEditModal(session)
    setEditingSessionName(session?.name ?? '')
    setEditingSessionDescription(session?.description ?? '')
    setEditingSessionTags(Array.isArray(session?.selection_tags) ? session.selection_tags.join(', ') : '')
    setEditingSessionTimeTag(session?.time_tag ?? '')
    setEditingSessionIsScript(session?.is_script ?? false)
    setEditingSessionPaidSexting(scriptPricingForm(session?.paid_sexting_config))
  }

  async function saveSessionName() {
    const s = showSessionEditModal
    if (!s?.id) return
    setSavingSessionName(true)
    onModelUpdate?.({ error: '' })
    try {
      const name = editingSessionName.trim() || s.name
      const description = editingSessionDescription.trim()
      const selection_tags = editingSessionTags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
      const rawTimeTag = editingSessionTimeTag ?? ''
      const time_tag = rawTimeTag === '' ? '' : rawTimeTag
      const is_script = editingSessionIsScript
      const updated = await updatePhotoSession(modelId, s.id, {
        name,
        description,
        selection_tags,
        is_script,
        time_tag,
        paid_sexting_config: scriptPricingPayload(editingSessionPaidSexting, is_script),
      })
      setSessions((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, ...updated, media_groups: x.media_groups } : x))
      )
      setShowSessionEditModal(null)
      addToast(t('toast.session.saved'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    } finally {
      setSavingSessionName(false)
    }
  }

  async function handleCreateGroup(sessionId) {
    if (!sessionId) return
    onModelUpdate?.({ error: '' })
    try {
      const g = await createMediaGroup(modelId, sessionId, '')
      if (g?.id) {
        setSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  media_groups: [...(s.media_groups || []), {
                    id: g.id,
                    name: g.name || '',
                    sort_order: g.sort_order ?? 0,
                    price_min: g.price_min,
                    price_max: g.price_max,
                    is_ready_for_sale: g.is_ready_for_sale ?? false,
                    missing_for_sale: g.missing_for_sale ?? ['price'],
                  }],
                }
              : s
          )
        )
      }
      addToast(t('toast.group.created'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    }
  }

  async function handleDeleteGroup() {
    const { group, session } = groupToDelete || {}
    if (!group?.id || !session?.id) return
    setDeletingGroup(true)
    onModelUpdate?.({ error: '' })
    try {
      await deleteMediaGroup(modelId, session.id, group.id)
      setSessions((prev) =>
        prev.map((s) =>
          s.id === session.id
            ? { ...s, media_groups: (s.media_groups || []).filter((g) => g.id !== group.id) }
            : s
        )
      )
      setMedia((prev) => {
        if (!prev) return prev
        return prev.map((m) =>
          m.media_group_id === group.id ? { ...m, media_group_id: null, group_sort_order: null } : m
        )
      })
      setGroupToDelete(null)
      addToast(t('toast.group.deleted'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    } finally {
      setDeletingGroup(false)
    }
  }

  async function handleSaveGroup(updatedGroup) {
    const { session, group } = groupToEdit || {}
    if (!session?.id || !group?.id) return
    const merged = { ...group, ...updatedGroup }
    const pm = merged.price_min
    const px = merged.price_max
    setSessions((prev) =>
      prev.map((s) =>
        s.id === session.id
          ? {
              ...s,
              media_groups: (s.media_groups || []).map((g) =>
                g.id === group.id
                  ? {
                      ...g,
                      ...merged,
                      is_ready_for_sale: isGroupReadyForSale(pm, px),
                      missing_for_sale: getGroupMissingForSale(pm, px),
                    }
                  : g
              ),
            }
          : s
      )
    )
    setGroupToEdit(null)
  }

  async function handleRemoveMediaFromSession(sessionId, mediaId) {
    if (!sessionId || !mediaId) return
    const ok = window.confirm('Remove this media from the script/session? The file will stay in the media library.')
    if (!ok) return
    onModelUpdate?.({ error: '' })
    try {
      await removeMediaFromPhotoSession(modelId, sessionId, mediaId)
      setMedia((prev) => {
        if (!prev) return prev
        return prev.map((m) =>
          m.id === mediaId
            ? { ...m, photo_session_id: null, media_group_id: null, group_sort_order: null, session_sort_order: null }
            : m
        )
      })
      loadSessions()
      loadMedia?.()
      addToast('Media removed from script/session. File stays in library.', 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
      loadMedia?.()
    }
  }

  async function handleMoveMediaToGroup(sessionId, groupId, mediaIds, dropIndex, onApplied) {
    if (!sessionId || mediaIds?.length === 0) return
    const idsSet = new Set(mediaIds)

    if (groupId == null) {
      // Перемещение в «Без группы»: убрать из текущих групп и обновить session_sort_order
      let orderedIdsForReorder = null
      const toRemoveFromGroup = []
      setMedia((prev) => {
        if (!prev) return prev
        const toMove = prev.filter((m) => idsSet.has(m.id) && m.photo_session_id === sessionId)
        if (toMove.length !== mediaIds.length) return prev
        toMove.forEach((m) => {
          if (m.media_group_id != null) toRemoveFromGroup.push({ id: m.id, groupId: m.media_group_id })
        })
        const ungrouped = prev
          .filter((m) => m.photo_session_id === sessionId && m.media_group_id == null && !idsSet.has(m.id))
          .sort((a, b) => (a.session_sort_order ?? 999999) - (b.session_sort_order ?? 999999))
        const insertAt = typeof dropIndex === 'number' ? Math.min(dropIndex, ungrouped.length) : ungrouped.length
        const before = ungrouped.slice(0, insertAt)
        const after = ungrouped.slice(insertAt)
        const newOrder = [...before, ...toMove, ...after]
        orderedIdsForReorder = newOrder.map((m) => m.id)
        const orderMap = Object.fromEntries(newOrder.map((m, i) => [m.id, i]))
        return prev.map((m) => {
          if (!idsSet.has(m.id) && (m.photo_session_id !== sessionId || m.media_group_id != null)) return m
          const idx = orderMap[m.id]
          if (idx == null) return m
          return {
            ...m,
            media_group_id: null,
            group_sort_order: null,
            session_sort_order: idx,
          }
        })
      })
      onApplied?.()
      try {
        for (const { id, groupId: gid } of toRemoveFromGroup) {
          await removeMediaFromGroup(modelId, sessionId, gid, id)
        }
        if (orderedIdsForReorder?.length) {
          await reorderPhotoSessionMedia(modelId, sessionId, orderedIdsForReorder)
        }
        loadMedia?.()
        addToast(mediaIds.length > 0 ? t('toast.media.movedToUngroupedCount', { count: mediaIds.length }) : t('toast.media.movedToUngrouped'), 'success')
      } catch (e) {
        const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
        onModelUpdate?.({ error: msg })
        loadMedia?.()
      }
      return
    }

    let orderedIdsForReorder = null
    setMedia((prev) => {
      if (!prev) return prev
      const toMove = prev.filter((m) => idsSet.has(m.id) && m.photo_session_id === sessionId)
      if (toMove.length !== mediaIds.length) return prev
      const targetGroupMedia = prev
        .filter((m) => m.photo_session_id === sessionId && m.media_group_id === groupId && !idsSet.has(m.id))
        .sort((a, b) => (a.group_sort_order ?? 999999) - (b.group_sort_order ?? 999999))
      const insertAt = typeof dropIndex === 'number' ? Math.min(dropIndex, targetGroupMedia.length) : targetGroupMedia.length
      const before = targetGroupMedia.slice(0, insertAt)
      const after = targetGroupMedia.slice(insertAt)
      const newOrder = [...before, ...toMove, ...after]
      orderedIdsForReorder = newOrder.map((m) => m.id)
      const orderMap = Object.fromEntries(newOrder.map((m, i) => [m.id, i]))
      return prev.map((m) => {
        const idx = orderMap[m.id]
        if (idx == null) return m
        return {
          ...m,
          ...(idsSet.has(m.id) ? { media_group_id: groupId } : {}),
          group_sort_order: idx,
        }
      })
    })
    onApplied?.()
    try {
      const res = await addMediaToGroup(modelId, sessionId, groupId, mediaIds)
      const added = res?.added ?? mediaIds.length
      if (orderedIdsForReorder?.length) {
        await reorderGroupMedia(modelId, sessionId, groupId, orderedIdsForReorder)
      }
      loadMedia?.()
      addToast(added > 0 ? t('toast.media.movedCount', { count: added }) : t('toast.media.moved'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
      loadMedia?.()
    }
  }

  async function handleReorderSessions(sessionIds) {
    if (!sessionIds?.length) return
    let prevSessions
    setSessions((prev) => {
      prevSessions = prev
      const idToOrder = Object.fromEntries(sessionIds.map((id, i) => [id, i]))
      return [...prev]
        .sort((a, b) => (idToOrder[a.id] ?? 999999) - (idToOrder[b.id] ?? 999999))
        .map((s, i) => ({ ...s, sort_order: i }))
    })
    try {
      await reorderPhotoSessions(modelId, sessionIds)
      addToast(t('toast.sessions.reordered'), 'success')
    } catch (e) {
      if (prevSessions) setSessions(prevSessions)
      const msg = getApiErrorMessage(e, t('toast.error.reorderFailed'))
      onModelUpdate?.({ error: msg })
    }
  }

  async function handleReorderGroups(sessionId, groupIds) {
    if (!sessionId || !groupIds?.length) return
    let prevSessions
    setSessions((prev) => {
      prevSessions = prev
      const idToOrder = Object.fromEntries(groupIds.map((id, i) => [id, i]))
      return prev.map((s) => {
        if (s.id !== sessionId) return s
        const sorted = [...(s.media_groups || [])]
          .filter((g) => g.id != null)
          .sort((a, b) => (idToOrder[a.id] ?? 999) - (idToOrder[b.id] ?? 999))
          .map((g, i) => ({ ...g, sort_order: i }))
        const ungrouped = (s.media_groups || []).filter((g) => g.id == null)
        return { ...s, media_groups: [...sorted, ...ungrouped] }
      })
    })
    try {
      await reorderMediaGroups(modelId, sessionId, groupIds)
      addToast(t('toast.groups.reordered'), 'success')
    } catch (e) {
      if (prevSessions) setSessions(prevSessions)
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    }
  }

  async function handleDeleteSession() {
    const s = sessionToDelete
    if (!s?.id) return
    setDeletingSession(true)
    onModelUpdate?.({ error: '' })
    try {
      await deletePhotoSession(modelId, s.id)
      setSessions((prev) => prev.filter((x) => x.id !== s.id))
      setMedia((prev) => {
        if (!prev) return prev
        return prev.map((m) =>
          m.photo_session_id === s.id
            ? { ...m, photo_session_id: null, session_sort_order: null, media_group_id: null, group_sort_order: null }
            : m
        )
      })
      setSessionToDelete(null)
      addToast(t('toast.session.deleted'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.moveFailed'))
      onModelUpdate?.({ error: msg })
    } finally {
      setDeletingSession(false)
    }
  }

  useEffect(() => {
    if (model) loadSessions()
  }, [model?.id])

  return {
    sessions,
    loadingSessions,
    moveTargetSessionId,
    setMoveTargetSessionId,
    moving,
    showSessionEditModal,
    setShowSessionEditModal,
    editingSessionName,
    setEditingSessionName,
    editingSessionDescription,
    setEditingSessionDescription,
    editingSessionTags,
    setEditingSessionTags,
    editingSessionTimeTag,
    setEditingSessionTimeTag,
    editingSessionIsScript,
    setEditingSessionIsScript,
    editingSessionPaidSexting,
    setEditingSessionPaidSexting,
    savingSessionName,
    groupToEdit,
    setGroupToEdit,
    handleSaveGroup,
    showCreateSessionInput,
    setShowCreateSessionInput,
    newSessionName,
    setNewSessionName,
    newSessionIsScript,
    setNewSessionIsScript,
    newSessionPaidSexting,
    setNewSessionPaidSexting,
    creatingSession,
    loadSessions,
    handleMoveToSession,
    handleCreateSessionAndMove,
    openSessionEdit,
    saveSessionName,
    sessionToDelete,
    setSessionToDelete,
    deletingSession,
    handleDeleteSession,
    groupToDelete,
    setGroupToDelete,
    deletingGroup,
    handleDeleteGroup,
    handleCreateGroup,
    handleReorderGroups,
    handleReorderSessions,
    handleMoveMediaToGroup,
    handleRemoveMediaFromSession,
  }
}
